/**
 * A theory question's marking guide: the examiner's model answer, stored in the
 * question's correct answer field as plain text with maths in `\(…\)`, as the
 * bank stores it.
 *
 * The student app splits it into numbered points on a new line or `;`, so the
 * same split is used here — the worker sees exactly what students get.
 */

/** Just enough of KaTeX to check an equation renders. */
export interface TexEngine {
  renderToString(tex: string, options?: { throwOnError?: boolean }): string;
}

/** The points a student sees, in order. Same rule as the student app. */
export function guidePoints(text: string): string[] {
  return text
    .split(/\n|;/)
    .map((point) => point.trim())
    .filter(Boolean);
}

/** `snippet` put in place of the selection, and where the cursor lands after it. */
export function insertAt(
  text: string,
  start: number,
  end: number,
  snippet: string,
): { text: string; cursor: number } {
  return {
    text: text.slice(0, start) + snippet + text.slice(end),
    cursor: start + snippet.length,
  };
}

/**
 * An equation wrapped for the guide. `\;` is LaTeX for a space, but the student
 * app would split the point on its `;`, so it becomes the near-identical `\:`.
 */
export function wrapEquation(latex: string): string {
  return `\\(${latex.replace(/\\;/g, "\\:")}\\)`;
}

/** The server's limit on the correct answer field, which holds the guide. */
export const GUIDE_MAX = 20_000;

const MATH = /\\\(([\s\S]*?)\\\)|\\\[([\s\S]*?)\\\]/g;

export const hasMath = (text: string) => /\\\(|\\\[/.test(text);

/**
 * Why the guide can't be saved, or null when it can. Every equation must be
 * closed within its point, and must render. With no engine (KaTeX failed to
 * load) only the closing is checked.
 */
export function guideProblem(text: string, engine: TexEngine | null): string | null {
  if (text.trim().length > GUIDE_MAX) {
    return `The marking guide is ${text.trim().length} characters. It can be at most ${GUIDE_MAX}.`;
  }
  const points = guidePoints(text);
  for (const [index, point] of points.entries()) {
    const label = `Point ${index + 1}`;
    if (/\\[()[\]]/.test(point.replace(MATH, ""))) {
      return `${label} has an equation that isn't closed. Check each \\( has a matching \\), with no ; or new line between them.`;
    }
    if (!engine) continue;
    for (const match of point.matchAll(MATH)) {
      const tex = match[1] ?? match[2] ?? "";
      try {
        engine.renderToString(tex, { throwOnError: true });
      } catch {
        return `${label} has an equation that can't be shown: ${tex}. Fix it before saving.`;
      }
    }
  }
  return null;
}
